import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatDate, formatDateOnly } from '../../services/adminService';

/**
 * One label/value pair. Kept local rather than exported: nothing else in the
 * app lays out fields this way yet, and a shared abstraction with one caller
 * would be guessing at the second caller's needs.
 */
const Field = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

/**
 * The heading for one section. All three read the same weight and size, and the
 * sections are separated by the space above the heading rather than a divider —
 * a divider landed right on top of the last field's bottom border and read as a
 * double rule.
 */
const SectionHeading = ({ children, sx }) => (
  <Typography
    variant="h6"
    gutterBottom
    sx={{
      fontSize: '1.125rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// Falls back to the raw value (not 'Not provided') when it fails to parse,
// so an operator sees the odd data rather than a message implying the field
// is empty.
const formatDob = (value) => {
  if (!value) return 'Not provided';
  return formatDateOnly(value) ?? value;
};

/**
 * Shared by the staff detail page and the self-service account page.
 *
 * `showSsn` and `showActivity` are not display preferences — they mark the
 * fields that only the admin endpoint returns. GET /user carries no
 * ssn_last_four, created_at, last_login or login_count at all, so rendering
 * them for the account page would print "Not provided" and "Never" beside a
 * login count of "undefined". They default to shown because that is the
 * existing staff behaviour; the account page opts out.
 */
function OverviewTab({ detail, showSsn = true, showActivity = true }) {
  const fullName = [detail.first_name, detail.middle_name, detail.last_name]
    .filter(Boolean)
    .join(' ');

  return (
    <Box>
      <SectionHeading>Overview</SectionHeading>

      <Field label="Name" value={fullName || 'Unnamed user'} />
      <Field label="Email" value={detail.username} />
      <Field label="User ID" value={`user #${detail.id}`} />
      <Field label="Date of birth" value={formatDob(detail.dob)} />
      {showSsn && (
        <Field
          label="SSN"
          value={detail.ssn_last_four ? `•••-••-${detail.ssn_last_four}` : 'Not provided'}
        />
      )}

      <SectionHeading sx={{ mt: 4 }}>Addresses</SectionHeading>
      {detail.addresses && detail.addresses.length > 0 ? (
        detail.addresses.map((address) => (
          <Box key={address.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2">{address.street}</Typography>
            <Typography variant="body2">
              {`${address.city}, ${address.state} ${address.zip_code}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {address.address_type}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No address on file
        </Typography>
      )}

      {showActivity && (
        <>
          <SectionHeading sx={{ mt: 4 }}>Activity</SectionHeading>
          <Field label="Joined" value={formatDate(detail.created_at)} />
          <Field label="Last login" value={formatDate(detail.last_login)} />
          <Field label="Login count" value={String(detail.login_count)} />
        </>
      )}
    </Box>
  );
}

export default OverviewTab;
